import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Slider,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Popover,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  Phone as CallIcon,
  CallEnd as HangUpIcon,
  Backspace as BackspaceIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Dialpad as DialpadIcon,
  VolumeUp as VolumeUpIcon,
  Analytics as AnalyticsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CallMade as CallMadeIcon,
  CallReceived as CallReceivedIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useWebPhoneContextV3 } from '../../contexts/WebPhoneContextV3';
import { CallStatus } from '../../hooks/useWebPhoneV3';
import brandingService from '../../services/branding.service';
import api from '../../services/api';

const WEBPHONE_VERSION = '3.00.09';

interface CallHistoryItem {
  id: number;
  remote_number: string;
  direction: 'inbound' | 'outbound' | 'internal';
  start_time: string;
  duration: number;
  duration_formatted: string;
  disposition: string;
  caller_id_name?: string;
}

const WebPhoneStandaloneV3: React.FC = () => {
  const {
    connectionStatus,
    callStatus,
    currentCall,
    isMuted,
    isOnHold,
    dialedNumber,
    errorMessage,
    // V3: volume, changeVolume, testSpeaker removed - no volume control
    makeCall,
    answerCall,
    hangUp,
    toggleMute,
    toggleHold,
    addDigit,
    deleteDigit,
    clearNumber,
    connect,
    // V3: rtcStats, eventLogs removed - no data collection
  } = useWebPhoneContextV3();

  const [showDialpad, setShowDialpad] = useState(true);
  const [branding, setBranding] = useState<{ logo_url: string; brand_name: string } | null>(null);
  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(null);
  const [callHistory, setCallHistory] = useState<CallHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const numberDisplayRef = useRef<HTMLDivElement>(null);
  const prevCallStatusRef = useRef<CallStatus>(callStatus);
  // V3: showStats removed - no stats panel

  // Load branding on mount
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const brandData = await brandingService.getCurrentBranding();
        setBranding(brandData);
      } catch (error) {
        console.error('[WebPhoneV3] Failed to load branding:', error);
      }
    };
    loadBranding();
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (connectionStatus === 'disconnected') {
      connect();
    }
  }, []);

  // Fetch call history
  const fetchCallHistory = async () => {
    setLoadingHistory(true);
    try {
      // Request last 30 calls regardless of date - use a 1 year lookback window
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      const startDate = oneYearAgo.toISOString().split('T')[0];
      const endDate = new Date().toISOString().split('T')[0];

      const response = await api.get('/endpoints/user-call-history-simple', {
        params: {
          per_page: 30,
          page: 1,
        },
      });
      if (response.data.success && response.data.data && Array.isArray(response.data.data.calls)) {
        setCallHistory(response.data.data.calls);
      } else {
        console.error('[WebPhoneV3] Failed to load call history or invalid data format');
        setCallHistory([]);
      }
    } catch (error) {
      console.error('[WebPhoneV3] Error loading call history:', error);
      setCallHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle opening call history
  const handleHistoryOpen = (event: React.MouseEvent<HTMLElement>) => {
    setHistoryAnchorEl(event.currentTarget);
    fetchCallHistory();
  };

  // Handle closing call history
  const handleHistoryClose = () => {
    setHistoryAnchorEl(null);
  };

  // Handle selecting a number from history
  const handleSelectNumber = (number: string) => {
    clearNumber();
    number.split('').forEach((digit) => addDigit(digit));
    handleHistoryClose();
  };

  // Keyboard input for number entry (PC only)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if not in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

      // Handle number keys (0-9, *, #)
      if (/^[0-9*#]$/.test(e.key)) {
        e.preventDefault();
        addDigit(e.key);
      }
      // Handle backspace
      else if (e.key === 'Backspace') {
        e.preventDefault();
        deleteDigit();
      }
      // Handle enter to make call
      else if (e.key === 'Enter' && dialedNumber && connectionStatus === 'registered' && callStatus === 'idle') {
        e.preventDefault();
        makeCall(dialedNumber);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dialedNumber, connectionStatus, callStatus, addDigit, deleteDigit, makeCall]);

  // Paste handler to extract clean number
  useEffect(() => {
    const handlePaste = (e: Event) => {
      // Only handle if not in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

      const clipboardEvent = e as ClipboardEvent;
      e.preventDefault();
      const pastedText = clipboardEvent.clipboardData?.getData('text') || '';
      // Extract only digits, *, and #
      const cleanNumber = pastedText.replace(/[^0-9*#]/g, '');
      if (cleanNumber) {
        clearNumber();
        cleanNumber.split('').forEach((digit) => addDigit(digit));
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [addDigit, clearNumber]);

  // Clear number when call ends
  useEffect(() => {
    // Check if we transitioned from 'in-call' to 'idle'
    if (prevCallStatusRef.current === 'in-call' && callStatus === 'idle') {
      clearNumber();
    }
    // Update ref with current status for next render
    prevCallStatusRef.current = callStatus;
  }, [callStatus, clearNumber]);

  // Format call duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get status display
  const getStatusDisplay = () => {
    if (connectionStatus === 'disconnected') return { icon: '✗', text: 'Disconnected', color: '#f44336' };
    if (connectionStatus === 'connecting') return { icon: '⏳', text: 'Connecting...', color: '#ff9800' };
    if (connectionStatus === 'connected') return { icon: '✓', text: 'Connected', color: '#4caf50' };
    if (connectionStatus === 'registered') return { icon: '✓', text: 'Registered', color: '#4caf50' };
    if (connectionStatus === 'error') return { icon: '✗', text: 'Error', color: '#f44336' };
    return { icon: '?', text: 'Unknown', color: '#9e9e9e' };
  };

  const getCallStatusDisplay = () => {
    if (callStatus === 'calling') return { text: '📞 Calling...', color: '#ff9800' };
    if (callStatus === 'ringing') return { text: '📞 Ringing...', color: '#ff9800' };
    if (callStatus === 'in-call' && currentCall) {
      const direction = currentCall.direction === 'outgoing' ? 'to' : 'from';
      const number = currentCall.remoteNumber;
      return { text: `Active Call ${direction} ${number}`, color: '#4caf50' };
    }
    return null;
  };

  const status = getStatusDisplay();
  const callStatusInfo = getCallStatusDisplay();

  // Dialpad keys
  const dialpadKeys = [
    { digit: '1', letters: '' },
    { digit: '2', letters: 'ABC' },
    { digit: '3', letters: 'DEF' },
    { digit: '4', letters: 'GHI' },
    { digit: '5', letters: 'JKL' },
    { digit: '6', letters: 'MNO' },
    { digit: '7', letters: 'PQRS' },
    { digit: '8', letters: 'TUV' },
    { digit: '9', letters: 'WXYZ' },
    { digit: '*', letters: '' },
    { digit: '0', letters: '+' },
    { digit: '#', letters: '' },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      {/* Header - Branded Banner */}
      <Box
        sx={{
          background: 'white',
          color: '#333',
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        {branding?.logo_url ? (
          <img
            src={branding.logo_url}
            alt={branding.brand_name || 'Logo'}
            style={{
              height: '45px',
              width: 'auto',
              display: 'block',
            }}
          />
        ) : (
          <>
            <CallIcon sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              WebPhone
            </Typography>
          </>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
        {/* Status Display */}
        <Chip
          label={
            callStatusInfo
              ? callStatusInfo.text
              : `${status.icon} v${WEBPHONE_VERSION} - ${status.text}`
          }
          size="small"
          sx={{
            width: '100%',
            mb: 2,
            fontWeight: 600,
            bgcolor: callStatusInfo ? callStatusInfo.color : status.color,
            color: 'white',
          }}
        />

        {/* Error Message */}
        {errorMessage && (
          <Box sx={{ mb: 2, p: 1, bgcolor: '#fee', borderRadius: 1 }}>
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          </Box>
        )}


        {/* Call Info (when in call) */}
        {currentCall && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {currentCall.remoteNumber}
            </Typography>
            {currentCall.status === 'connected' && (
              <Typography variant="body2" color="text.secondary">
                {formatDuration(currentCall.duration)}
              </Typography>
            )}
          </Box>
        )}

        {/* Number Display with History */}
        <Box sx={{ mb: 2, position: 'relative' }}>
          <Paper
            ref={numberDisplayRef}
            variant="outlined"
            onClick={handleHistoryOpen}
            sx={{
              p: 2,
              minHeight: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: '#f8f9fa',
              borderColor: '#e9ecef',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: '#e9ecef',
              },
            }}
          >
            <IconButton size="small" sx={{ color: '#666' }}>
              <HistoryIcon />
            </IconButton>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: dialedNumber ? '#333' : '#adb5bd',
                wordBreak: 'break-all',
                flex: 1,
                textAlign: 'right',
              }}
            >
              {dialedNumber || 'Enter number'}
            </Typography>
          </Paper>

          {/* Call History Popover */}
          <Popover
            open={Boolean(historyAnchorEl)}
            anchorEl={historyAnchorEl}
            onClose={handleHistoryClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            sx={{
              mt: 1,
            }}
            slotProps={{
              paper: {
                sx: { minWidth: 300, maxWidth: 400 },
              },
            }}
          >
            {loadingHistory ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : callHistory.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No recent calls
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {callHistory.map((call) => (
                  <ListItemButton
                    key={call.id}
                    onClick={() => handleSelectNumber(call.remote_number)}
                    sx={{
                      borderBottom: '1px solid #e0e0e0',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <ListItemIcon>
                      {call.direction === 'outbound' ? (
                        <CallMadeIcon color="primary" />
                      ) : (
                        <CallReceivedIcon color="success" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={call.remote_number}
                      secondary={
                        <>
                          {call.caller_id_name && <span>{call.caller_id_name} • </span>}
                          {call.duration_formatted} • {new Date(call.start_time).toLocaleString()}
                        </>
                      }
                      primaryTypographyProps={{
                        fontWeight: 600,
                        fontSize: '0.95rem',
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Popover>
        </Box>

        {/* Dialpad (show only when not in call or if toggled) */}
        {(callStatus === 'idle' || showDialpad) && (
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {dialpadKeys.map((key) => (
              <Grid item xs={4} key={key.digit}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => addDigit(key.digit)}
                  sx={{
                    height: 60,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    bgcolor: '#f8f9fa',
                    borderColor: '#e9ecef',
                    '&:hover': {
                      bgcolor: '#e9ecef',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                    {key.digit}
                  </Typography>
                  {key.letters && (
                    <Typography variant="caption" sx={{ color: '#666', fontSize: 10 }}>
                      {key.letters}
                    </Typography>
                  )}
                </Button>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Action Buttons */}
        <Grid container spacing={1}>
          {callStatus === 'idle' && (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CallIcon />}
                  onClick={() => makeCall(dialedNumber)}
                  disabled={connectionStatus !== 'registered' || !dialedNumber || callStatus !== 'idle'}
                  sx={{
                    bgcolor: '#4caf50',
                    '&:hover': { bgcolor: '#45a049' },
                  }}
                >
                  Call
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<BackspaceIcon />}
                  onClick={deleteDigit}
                  disabled={!dialedNumber}
                >
                  Delete
                </Button>
              </Grid>
            </>
          )}

          {callStatus === 'calling' && (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<HangUpIcon />}
                onClick={hangUp}
                sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
              >
                Cancel
              </Button>
            </Grid>
          )}

          {callStatus === 'ringing' && currentCall?.direction === 'outgoing' && (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<HangUpIcon />}
                onClick={hangUp}
                sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
              >
                Cancel
              </Button>
            </Grid>
          )}

          {callStatus === 'ringing' && currentCall?.direction === 'incoming' && (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CallIcon />}
                  onClick={answerCall}
                  sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
                >
                  Answer
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<HangUpIcon />}
                  onClick={hangUp}
                  sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
                >
                  Decline
                </Button>
              </Grid>
            </>
          )}

          {callStatus === 'in-call' && (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={isMuted ? <MicOffIcon /> : <MicIcon />}
                  onClick={toggleMute}
                  sx={{
                    bgcolor: isMuted ? '#ff9800' : 'transparent',
                    color: isMuted ? 'white' : 'inherit',
                  }}
                >
                  {isMuted ? 'Unmute' : 'Mute'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={isOnHold ? <PlayIcon /> : <PauseIcon />}
                  onClick={toggleHold}
                  sx={{
                    bgcolor: isOnHold ? '#9c27b0' : 'transparent',
                    color: isOnHold ? 'white' : 'inherit',
                  }}
                >
                  {isOnHold ? 'Resume' : 'Hold'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DialpadIcon />}
                  onClick={() => setShowDialpad(!showDialpad)}
                >
                  {showDialpad ? 'Hide' : 'Show'} Keys
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<HangUpIcon />}
                  onClick={hangUp}
                  sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
                >
                  Hang Up
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default WebPhoneStandaloneV3;
